import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact as AgGridReactType } from "ag-grid-react";

// import { ColDef} from "ag-grid-community";
import { CompleteJson, Table } from "../../../types/DealTracker";
import {
  useUpdateAdminCustomerSheetMutation,
  useUpdateCustomerSheetPinMutation,
  useDeleteAdminCustomerSheetMutation,
} from "../../../store/apiSlices/adminCustomerSheets";

import RowDeleteBtn from "../../DealTracker/RowDeleteBtn";
import { toast } from "react-toastify";
import AddColBtn from "../../DealTracker/AddColBtn";
import { toSnakeKey } from "../../../utils";
import {
  cloneInitialRow,
  generateColumnDefs,
  inferDataType,
  makeColDef,
} from "../../../utils/agGridUtils";
import {
  CellValueChangedEvent,
  ColDef,
  ColumnMovedEvent,
} from "ag-grid-community";

import MyInnerHeaderComponent from "../../DealTracker/MyInnerHeaderComponent";
import DealsTableCard from "../../DealTracker/DealsTableCard";

import { initialRowData } from "./data";

const desireOrder = Object.entries(initialRowData).map(([field]) => field);

export default function CustomerSheet({
  table,
  minOrder,
  isFetching,
}: {
  table: Table;
  minOrder: number;
  isFetching: boolean;
}) {
  const gridRef = useRef<AgGridReactType<any>>(null);

  // Row Data: The data to be displayed.
  const [rowData, setRowData] = useState<CompleteJson[]>(
    () => table.complete_json ?? [],
  );
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [hiddenColsCount, setHiddenColsCount] = useState(0);

  const [isTableOpen, setIsTableOpen] = useState(() => {
    // initially open if pinned or topmost
    return table.is_pin === 1 || table.table_order === minOrder;
  });

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // reactively open/close when pinned changes
    setIsTableOpen(table.is_pin === 1 || table.table_order === minOrder);
  }, [table.is_pin, table.table_order, minOrder]);

  useEffect(() => {
    if (!table.complete_json) return;

    setRowData(table.complete_json);

    const generated = generateColumnDefs(table.complete_json[0]);
    setColumnDefs(generated);
  }, [table.complete_json]);

  useEffect(() => {
    const handleCloseAll = () => {
      setIsTableOpen(false);
    };

    window.addEventListener("close-all-tables", handleCloseAll);
    return () => window.removeEventListener("close-all-tables", handleCloseAll);
  }, []);

  const [updateSheet] = useUpdateAdminCustomerSheetMutation();
  const [updateSheetPinned] = useUpdateCustomerSheetPinMutation();
  const [handleDeleteSheet] = useDeleteAdminCustomerSheetMutation();

  const addColumn = (
    field: string,
    data_type: "string" | "number" | "boolean" | "date" | "select" = "string",
    options: string[] = [],
  ) => {
    const snakeCaseField = toSnakeKey(field);

    if (columnDefs.find((col) => col.field === snakeCaseField)) {
      toast.warning("Column already exists");
      return;
    }

    const newCol = makeColDef(snakeCaseField, data_type, options);

    // 🔥 update local state
    setColumnDefs((prev) => [...prev, newCol]);

    // update existing rowData so new col has empty values
    const rowData: any[] = [];
    gridRef.current?.api.forEachNode((node) => {
      rowData.push({
        ...node.data,
        [snakeCaseField]: {
          column_value: "",
          column_type: {
            data_type,
            options,
            color_code: "",
            additional_info: "",
          },
        },
      });
    });

    gridRef.current?.api.setGridOption("rowData", rowData);

    handleSave();
  };

  const handleSave = async () => {
    const allData: any[] = [];
    gridRef.current?.api.forEachNode((node) => {
      allData.push(node.data);
    });
    // console.log(allData);

    await updateSheet({ id: table.id, complete_json: allData });
  };

  // Add a new row
  const handleAddRow = useCallback(() => {
    if (!gridRef.current) return;

    const newRow = cloneInitialRow(initialRowData as any);

    gridRef.current.api.applyTransaction({ add: [newRow] });

    handleSave();
  }, [table]);

  const onCellValueChanged = async (param: CellValueChangedEvent) => {
    const field = param.colDef.field;
    if (!field) return;

    // if (field === "flow") {
    //   const payload = {
    //     row_id: param.data.row_id,
    //     flow_template_id: param.newValue,
    //   };
    //   await addTradeFlow(payload);
    // }

    const cell = param.data[field];

    if (
      cell?.column_type?.data_type === "date" &&
      cell.column_value instanceof Date
    ) {
      cell.column_value = cell.column_value.toISOString();
    }

    // update just this row in grid
    param.api.applyTransaction({ update: [param.data] });

    handleSave();
  };

  const deleteColumn = useCallback(
    (colId: keyof CompleteJson) => {
      if (!gridRef.current) return;

      // define dependency rules
      const dependentCols: Record<string, string[]> = {
        payables: ["payables", "payables_status"],
        receivables: ["receivables", "receivables_status"],
      };

      // figure out what to delete
      const colsToDelete = dependentCols[colId] ?? [colId];

      // update column defs
      setColumnDefs((prev) =>
        prev.filter((col) => !colsToDelete.includes(col.field as string)),
      );

      // update row data (complete_json)
      const updatedCompleteJson: CompleteJson[] = [];
      gridRef.current.api.forEachNode((node) => {
        const newRow = { ...node.data };
        colsToDelete.forEach((col) => {
          delete newRow[col];
        });
        updatedCompleteJson.push(newRow);
      });

      // update server
      updateSheet({
        id: table.id,
        complete_json: updatedCompleteJson,
      });
    },
    [table, updateSheet],
  );

  const allColumnDefs = useMemo(() => {
    const generatedColDefs = generateColumnDefs(table.complete_json[0]);

    let order: string[];

    const saved = localStorage.getItem(
      `customer-sheet-columnOrder-${table.id}`,
    );

    if (saved) {
      const fields = JSON.parse(saved);
      order = fields;
    } else {
      order = desireOrder;
    }

    const ordered = order
      .map((key) =>
        generatedColDefs.find((col) => col.colId === key || col.field === key),
      )
      .filter(Boolean) as ColDef[];

    const remaining = generatedColDefs.filter(
      (col) => !order.includes(col.colId || col.field || ""),
    );

    // Filter out deleted columns
    const orderedCols = [...ordered, ...remaining];

    return orderedCols.concat({
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      maxWidth: 60,
      cellRenderer: RowDeleteBtn,
      cellRendererParams: {
        handleSave,
      },
      pinned: "right",
      headerComponent: AddColBtn,
      headerComponentParams: {
        addColumn,
      },
      editable: false,
    });
  }, [columnDefs]);

  const defaultColdefs = useMemo(
    (): ColDef => ({
      editable: true,
      filter: true,

      valueGetter: (params: any) => {
        const field = params.colDef.field;
        const cell = params.data?.[field];
        return typeof cell === "object" && "column_value" in cell
          ? cell.column_value
          : cell; // fallback to primitive
      },

      valueSetter: (params: any) => {
        const field = params.colDef.field;
        const cell = params.data?.[field];

        if (typeof cell === "object" && "column_value" in cell) {
          cell.column_value = params.newValue;
          return true;
        }

        if (
          cell === undefined ||
          typeof cell !== "object" ||
          !("column_value" in cell)
        ) {
          const dataType = inferDataType(params.newValue);

          const newCell = {
            column_value: params.newValue,
            column_type: {
              data_type: dataType,
              color_code: "",
              additional_info: "",
            },
          };
          params.data[field] = newCell;
          return true;
        }

        // fallback (e.g., for fields like 'flow' or 'company_name')
        params.data[field] = params.newValue;
        return true;
      },

      headerComponentParams: {
        innerHeaderComponent: MyInnerHeaderComponent,
        deleteColumn,
      },
    }),
    [],
  );

  const onColumnMoved = (params: ColumnMovedEvent) => {
    const columnState = params.api.getColumnState();
    const orderedFields = columnState.map((col) => col.colId);
    localStorage.setItem(
      `customer-sheet-columnOrder-${table.id}`,
      JSON.stringify(orderedFields),
    );
  };

  const onBtnCsvExport = useCallback(() => {
    if (gridRef.current) gridRef.current.api.exportDataAsCsv();
  }, []);

  const onColumnVisible = useCallback(() => {
    if (!gridRef.current) return;

    const excludedCols = ["row_id", "payables_status", "receivables_status"];

    const hiddenCount = gridRef.current.api
      .getAllGridColumns()
      .filter(
        (col) => !excludedCols.includes(col.getColId()) && !col.isVisible(),
      ).length;

    setHiddenColsCount(hiddenCount);
  }, []);

  const handleShowAllHidenCol = () => {
    if (!gridRef.current) return;

    const excludedCols = ["row_id", "payables_status", "receivables_status"];

    const colIds = gridRef.current.api
      .getAllGridColumns()
      .map((col) => col.getColId())
      .filter((id) => !excludedCols.includes(id));

    gridRef.current.api.setColumnsVisible(colIds, true);
  };

  const toggleTableOpen = () => setIsTableOpen((prev) => !prev);

  const toggleExpand = () => {
    setIsExpanded((prev) => {
      window.dispatchEvent(new CustomEvent("expand-dealtracker-table"));
      return !prev;
    });
  };

  return (
    <DealsTableCard
      {...{
        isFetching,
        table,
        rowData,
        isTableOpen,
        isExpanded,
        toggleTableOpen,
        toggleExpand,
        handleAddRow,
        hiddenColsCount,
        handleShowAllHidenCol,
        onBtnCsvExport,
        handleUpdatePinned: updateSheetPinned,
        handleDeleteTable: handleDeleteSheet,
      }}
    >
      <DealsTableCard.Header>
        <DealsTableCard.HeaderLeftSide>
          <DealsTableCard.Toggle />
          <DealsTableCard.AdminCustomerSheetTitle />
          <DealsTableCard.SaveStatus />
          <DealsTableCard.PinnedBadge />
        </DealsTableCard.HeaderLeftSide>

        <DealsTableCard.Actions>
          <DealsTableCard.CreateRowBtn />
          <DealsTableCard.PinBtn />
          <DealsTableCard.ShowHiddenBtn />
          <DealsTableCard.ExportFileBtn />
          <DealsTableCard.DeleteBtn />
          <DealsTableCard.ExpandBtn />
        </DealsTableCard.Actions>
      </DealsTableCard.Header>

      <DealsTableCard.Body>
        <AgGridReact
          key={`admmin-customer-sheet-${table.id}`}
          theme={themeQuartz}
          ref={gridRef}
          rowData={rowData}
          columnDefs={allColumnDefs}
          rowDragManaged={true}
          rowDragEntireRow={true}
          defaultColDef={defaultColdefs}
          onCellValueChanged={onCellValueChanged}
          maintainColumnOrder={true}
          onColumnMoved={onColumnMoved}
          onRowDragEnd={handleSave}
          onColumnVisible={onColumnVisible}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20]}
          getRowId={(params) => {
            if (params.data.row_id) return params.data.row_id;
          }}
          context={{
            handleSave,
            table_id: table.id,
            permission: "owner",
            uid: table.uid,
          }}
        />
      </DealsTableCard.Body>
    </DealsTableCard>
  );
}
